import type { KineticCoreState } from "@cloudnest/kinetic-core-engine";

interface GameOverScreenProps {
  readonly gameState: KineticCoreState;
  readonly isMobile: boolean;
  readonly onNewGame: () => void;
}

const REASON_LABELS: Record<string, string> = {
  ALL_ELIMINATED: "Opponent eliminated.",
  SELF_ELIMINATED: "Self-elimination.",
  BOTH_ELIMINATED: "Both eliminated.",
  TURN_LIMIT: "Turn limit reached.",
  CONCEDE: "Concession.",
};

const overlayStyle: React.CSSProperties = {
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "rgba(0, 0, 0, 0.7)",
  borderRadius: "8px",
};

const titleStyle: React.CSSProperties = {
  fontSize: "34px",
  fontWeight: "bold",
  color: "var(--cn-alkagi-stone-white)",
  marginBottom: "10px",
};

const reasonStyle: React.CSSProperties = {
  fontSize: "15px",
  color: "var(--cn-alkagi-text-muted)",
  marginBottom: "20px",
};

const buttonStyle: React.CSSProperties = {
  padding: "10px 24px",
  fontSize: "16px",
  fontWeight: "bold",
  border: "none",
  borderRadius: "6px",
  backgroundColor: "var(--cn-alkagi-board-fill)",
  color: "var(--cn-alkagi-text)",
  cursor: "pointer",
};

export function GameOverScreen({
  gameState,
  isMobile,
  onNewGame,
}: GameOverScreenProps) {
  const winnerName = gameState.winner
    ? (gameState.participants.find(
        (participant) => participant.id === gameState.winner,
      )?.name ?? "Unknown")
    : null;

  const winnerText = gameState.isDraw
    ? "Draw"
    : winnerName
      ? `${winnerName} wins`
      : "Match ended";

  const reasonText = gameState.outcomeReason
    ? (REASON_LABELS[gameState.outcomeReason] ?? gameState.outcomeReason)
    : "";

  return (
    <div data-testid="game-over-overlay" style={overlayStyle}>
      <div
        data-testid="winner-display"
        style={{
          ...titleStyle,
          fontSize: isMobile ? "24px" : "34px",
          marginBottom: isMobile ? "8px" : "10px",
        }}
      >
        {winnerText}
      </div>
      <div
        data-testid="victory-reason-display"
        style={{
          ...reasonStyle,
          fontSize: isMobile ? "13px" : "15px",
          marginBottom: isMobile ? "14px" : "20px",
        }}
      >
        {reasonText}
      </div>
      <button
        data-testid="game-over-new-game-btn"
        type="button"
        style={buttonStyle}
        onClick={onNewGame}
      >
        New Game
      </button>
    </div>
  );
}
